import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Inbox, Search, Database, LayoutDashboard, Bell, CheckCircle2 } from "lucide-react";
import Reveal from "../components/Reveal";
import SectionDivider from "../components/SectionDivider";
import SEO from "../components/SEO";

const stages = [
  { n: "01", title: "Document Received", icon: Inbox, body: "A document arrives in your designated business inbox. Could be an invoice, contract, proposal, or any business document." },
  { n: "02", title: "Automatic Detection", icon: Search, body: "Our system instantly detects the new document and begins processing it in the background. No triggers needed from your team." },
  { n: "03", title: "Data Extraction", icon: Database, body: "Key information is extracted from the document — sender details, amounts, deadlines, and a plain-English summary of what it contains and what action may be required." },
  { n: "04", title: "Dashboard Update", icon: LayoutDashboard, body: "Everything gets logged automatically into your business dashboard. Searchable, sortable, and always up to date." },
  { n: "05", title: "Instant Notification", icon: Bell, body: "You receive a clean summary notification with all the extracted data. For urgent documents, a separate priority alert fires immediately." },
  { n: "06", title: "Done", icon: CheckCircle2, body: "Total time from document received to you being informed: under 30 seconds." },
];

const HowItWorks = () => {
  const navigate = useNavigate();
  return (
    <div data-testid="how-it-works-page">
      <SEO
        title="How It Works — Zelaflo Labs Automation Process"
        description="See how Zelaflo Labs automates your document workflow from inbox to insight in under 30 seconds. No manual work required."
        path="/how-it-works"
      />
      {/* HERO */}
      <section className="relative pt-40 pb-20 overflow-hidden" data-testid="how-hero">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 z-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#7B2FBE] font-semibold mb-3">Process</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tighter max-w-4xl text-balance" style={{ color: "var(--text)" }}>
              Automation That Works <span className="text-[#7B2FBE]">While You Sleep.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-lg md:text-xl max-w-2xl" style={{ color: "var(--text-muted)" }}>
              Here's exactly what happens from the moment a document hits your inbox.
            </p>
          </Reveal>
        </div>
      </section>

      <SectionDivider />

      {/* TIMELINE */}
      <section className="relative py-20 md:py-28" data-testid="timeline-section">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="relative">
            {/* central vertical line */}
            <motion.div
              className="absolute left-6 md:left-1/2 md:-translate-x-1/2 top-0 w-[2px] bg-gradient-to-b from-[#6B2FD9] via-[#6B2FD9]/40 to-transparent"
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.5, ease: "easeOut" }}
              style={{ transformOrigin: "top", height: "100%" }}
              aria-hidden
            />

            <div className="space-y-16 md:space-y-24">
              {stages.map((s, i) => {
                const isLeft = i % 2 === 0;
                return (
                  <Reveal key={i} delay={i * 0.05}>
                    <div className="relative grid md:grid-cols-2 gap-8 items-center">
                      {/* dot */}
                      <motion.div
                        className="absolute left-6 md:left-1/2 md:-translate-x-1/2 z-10"
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                      >
                        <div className="w-6 h-6 rounded-full bg-[#6B2FD9] ring-8 ring-[#171412] shadow-[0_0_30px_rgba(107,47,217,0.7)]" />
                      </motion.div>

                      <div className={`pl-16 md:pl-0 ${isLeft ? "md:pr-16 md:text-right" : "md:order-2 md:pl-16"}`}>
                        <span className="font-heading text-[#6B2FD9]/40 text-7xl font-bold tracking-tighter block">{s.n}</span>
                      </div>

                      <div className={`pl-16 md:pl-0 ${isLeft ? "md:order-2 md:pl-16" : "md:pr-16 md:text-right"}`}>
                        <div className={`inline-flex flex-col gap-4 ${isLeft ? "" : "md:items-end"}`}>
                          <div className="w-12 h-12 rounded-xl bg-[#6B2FD9]/15 border border-[#6B2FD9]/30 flex items-center justify-center text-[#8A4FFF]">
                            <s.icon size={20} />
                          </div>
                          <h3 className="font-heading text-2xl md:text-3xl font-semibold text-white tracking-tight">{s.title}</h3>
                          <p className="text-white/60 leading-relaxed max-w-md">{s.body}</p>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-28" data-testid="how-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-[#6B2FD9] p-12 md:p-20 text-center">
              <div className="absolute inset-0 opacity-30 dot-grid" />
              <motion.div
                className="absolute -left-20 -top-20 w-72 h-72 rounded-full bg-white/10 blur-3xl"
                animate={{ x: [0, 60, 0], y: [0, 40, 0] }}
                transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                className="absolute -right-20 -bottom-20 w-72 h-72 rounded-full bg-black/30 blur-3xl"
                animate={{ x: [0, -50, 0], y: [0, -30, 0] }}
                transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
              />
              <div className="relative">
                <h2 className="font-heading text-4xl md:text-6xl font-semibold text-white tracking-tighter max-w-3xl mx-auto text-balance">
                  See It Work On Your Own Documents
                </h2>
                <p className="mt-6 text-white/85 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
                  Send us any real document from your business. We'll run it through our system and send you back exactly what gets extracted — free, with no commitment.
                </p>
                <a
                  href="/contact"
                  onClick={(e) => { e.preventDefault(); navigate("/contact"); }}
                  className="mt-10 inline-flex items-center gap-2 bg-white text-[#171412] px-8 py-4 rounded-full text-sm font-bold hover:bg-white/90 hover:scale-105 transition-all duration-300"
                  data-testid="how-send-sample-btn"
                >
                  Send Your Free Sample
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
